// @ts-check
// Menu de clique direito na linha da tabela e no card do kanban (doc 15, seção 6.2): as ações da seleção do registro
// de comandos, as automações manuais e "Mais ações", que abre o menu de comandos. Clicar com o botão direito numa
// linha fora da seleção seleciona só ela; dentro da seleção, as ações valem para todos os selecionados.
import { html } from 'htm/preact';
import { useMemo, useState } from 'preact/hooks';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { Dropdown, DropdownMenuHeader, DropdownMenuItems, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { t } from '../../i18n/index.js';
import { SelectionAutomationMenuItems } from '../automations/manual-automations.js';
import { openCommandMenu } from '../side-panel/side-panel-state.js';
import { getCommandLabel, getSelectionCommands, getStageChoices, runCommand } from './command-actions.js';

export { selectionForContextMenu } from '../../lib/command-registry.js';

/**
 * Âncora virtual na posição do mouse, para o Dropdown abrir onde a pessoa clicou.
 * @param {{ x: number, y: number }} position
 */
function pointAnchor(position) {
  return { current: /** @type {any} */ ({ getBoundingClientRect: () => ({ top: position.y, bottom: position.y, left: position.x, right: position.x, width: 0, height: 0 }), contains: () => false }) };
}

/**
 * @param {{ target: import('./command-state.js').SelectionTarget, position: { x: number, y: number }, onClose: () => void }} props
 */
export function RecordContextMenu({ target, position, onClose }) {
  const [mode, setMode] = useState(/** @type {'root' | 'stage'} */ ('root'));
  const anchorRef = useMemo(() => pointAnchor(position), [position.x, position.y]);
  const commands = getSelectionCommands(target);
  const stageChoices = getStageChoices(target);
  const run = (/** @type {import('../../lib/command-registry.js').CommandDescriptor} */ command, /** @type {{ stageValue?: string | null }} */ options = {}) => {
    onClose();
    runCommand(command, { selection: target }, options);
  };
  const stageCommand = commands.find((command) => command.id === 'selection.changeStage');

  return html`<${Dropdown} anchorRef=${anchorRef} isOpen=${true} onClose=${onClose} width=${240} offset=${0} className="record-context-menu">
    ${mode === 'stage' && stageCommand && stageChoices
      ? html`<${DropdownMenuHeader} title=${getCommandLabel(stageCommand)} onBack=${() => setMode('root')} />
        <${DropdownMenuItems} hasMaxHeight=${true}>
          ${stageChoices.map((option) => html`<${MenuItem}
            key=${option.value ?? ''}
            leftComponent=${html`<${Tag} color=${option.color} text=${option.label} variant=${option.value === null ? 'outline' : 'solid'} />`}
            text=""
            rightComponent=${option.isHandoff ? html`<${Icon} name="IconLogout" size=${14} />` : undefined}
            onClick=${() => run(stageCommand, { stageValue: option.value })}
          />`)}
        <//>`
      : html`<${DropdownMenuItems}>
          ${commands.map((command) => html`<${MenuItem}
            key=${command.id}
            icon=${command.icon}
            text=${getCommandLabel(command)}
            accent=${command.accent ?? 'default'}
            hasSubMenu=${command.id === 'selection.changeStage' && Boolean(stageChoices)}
            onClick=${() => (command.id === 'selection.changeStage' && stageChoices ? setMode('stage') : run(command))}
          />`)}
          ${target.isTrashMode
            ? null
            : html`<${SelectionAutomationMenuItems} objectNameSingular=${target.objectMetadata.nameSingular} selectedCount=${target.count}
                resolveSelectedIds=${target.resolveSelectedIds} onDone=${target.onChanged} onPick=${onClose} />`}
        <//>
        <${DropdownMenuSeparator} />
        <${DropdownMenuItems}>
          <${MenuItem} icon="IconLayoutSidebarRightExpand" text=${t('commands.contextMenu.moreActions')} onClick=${() => {
            onClose();
            openCommandMenu();
          }} />
        <//>`}
  <//>`;
}
